import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useInView } from 'react-intersection-observer';
import { useState, useCallback } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  lowQuality?: boolean;
  width?: number;
  height?: number;
  priority?: boolean;
  onLoad?: (img: HTMLImageElement) => void;
}

export default function LazyImage({ 
  src, 
  alt, 
  className = '', 
  lowQuality = false,
  width,
  height,
  priority = false,
  onLoad,
}: LazyImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: priority ? '200px 0px' : '50px 0px',
    skip: priority, // Skip intersection observer for priority images
  });

  // Function to get optimized image URL with WebP support and responsive sizing
  const getOptimizedImageUrl = useCallback((url: string, format: 'webp' | 'original' = 'webp') => {
    if (!url) return url;
    
    // Check if it's an Unsplash image
    if (url.includes('unsplash.com')) {
      const quality = lowQuality ? 'q=60' : 'q=80';
      const size = width ? `w=${Math.min(width * 2, 1200)}` : 'w=800'; // 2x for retina, max 1200px
      const formatParam = format === 'webp' ? 'fm=webp' : '';
      
      const separator = url.includes('?') ? '&' : '?';
      const params = [quality, size, formatParam, 'auto=format'].filter(Boolean).join('&');
      return `${url}${separator}${params}`;
    }
    
    return url;
  }, [lowQuality, width]);

  // Generate srcSet for responsive images
  const generateSrcSet = useCallback((url: string) => {
    if (!url.includes('unsplash.com')) return undefined;
    
    const sizes = [400, 800, 1200];
    return sizes.map(size => {
      const quality = lowQuality ? 'q=60' : 'q=80';
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}w=${size}&${quality}&fm=webp&auto=format ${size}w`;
    }).join(', ');
  }, [lowQuality]);

  const handleImageLoad = useCallback((event: any) => {
    setIsLoaded(true);
    if (onLoad && event.target) {
      onLoad(event.target as HTMLImageElement);
    }
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  // Fallback image for errors
  const fallbackSrc = "/images/fallback-image.png";

  // Show immediately for priority images or when in view
  const shouldShow = priority || inView;

  return (
    <div ref={ref} className={`${className} relative overflow-hidden`}>
      {shouldShow ? (
        <picture>
          {/* WebP source for modern browsers */}
          <source 
            srcSet={generateSrcSet(src)} 
            type="image/webp"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          
          {/* Fallback for older browsers */}
          <LazyLoadImage
            src={imageError ? fallbackSrc : getOptimizedImageUrl(src, 'original')}
            alt={alt}
            effect={priority ? undefined : "blur"}
            loading={priority ? "eager" : "lazy"}
            className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3Crect width='1' height='1' fill='%23f3f4f6'/%3E%3C/svg%3E"
            threshold={100}
            visibleByDefault={priority}
            onLoad={handleImageLoad}
            onError={handleImageError}
            width={width}
            height={height}
          />
        </picture>
      ) : (
        // Placeholder while not in view
        <div 
          className={`${className} bg-gray-100 dark:bg-gray-800 animate-pulse flex items-center justify-center`}
          style={{ width, height }}
        >
          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      )}
      
      {/* Loading indicator */}
      {shouldShow && !isLoaded && !imageError && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-spin"></div>
        </div>
      )}
    </div>
  );
}